export type AssetNamespace = 'client' | 'server';

export interface Asset {
  readonly path: string;
  readonly namespace: AssetNamespace;
  readonly mimeType: string;
  readonly bytes: Uint8Array;
  readonly size: number;
  text(): string;
  json<T = unknown>(): T;
}

export type AssetAction = 'updated' | 'removed';

/** Who performed an asset write — recorded for the master audit trail. */
export type AssetChangedBy =
  | { type: 'area'; areaId: string }
  | { type: 'admin'; adminId: string };

/**
 * The payload delivered to `onAssetUpdated`/`onAssetRemoved` (system hooks and
 * the master audit hooks). The verb is carried by the hook/method name (and by
 * the `AssetAction` discriminator on the wire), so it is not repeated here.
 */
export interface AssetChange {
  readonly namespace: AssetNamespace;
  readonly path: string;
  /** Bytes written (updated) or freed (removed). */
  readonly byteSize: number;
  readonly changedBy: AssetChangedBy;
}

export function makeAsset(
  namespace: AssetNamespace,
  path: string,
  bytes: Uint8Array,
  mimeType: string,
): Asset {
  const text = () => new TextDecoder().decode(bytes);
  return {
    path,
    namespace,
    mimeType,
    bytes,
    size: bytes.byteLength,
    text,
    // Reuses text()'s decode instead of re-decoding bytes independently.
    json: <T = unknown>() => JSON.parse(text()) as T,
  };
}
